/**
 * work-storage.js — where a person's WORK lives on this device.
 *
 * A person's work here means state that cost them effort and that nothing can
 * recompute: a thumbnail design, a censor pass over an hour of video, a join
 * queue with its settings. It is not a cache. This module decides where such
 * state is kept, so that each store that holds it (tool-draft.js,
 * transcript-checkpoint-store.js) gets the same answer.
 *
 * ── Why not the site's default storage ──────────────────────────────────────
 * Chromium evicts whole NON-persistent storage buckets, least recently used
 * first, once the disk has less free space than «should remain available»
 * (min 2 GB, 10 %). The first round runs 5 minutes after the browser starts,
 * then every 30 minutes, and an open page is no exception
 * (QuotaDatabase::GetBucketsForEviction). The default bucket also holds the
 * gigabytes we can download again (model caches, picked-file copies), so it is
 * the first to go when the disk fills, and before 27.09.2026 the work went
 * with it (support chat 640: two hours of censor edits, device_cap.su
 * 306 MB → 0 while the login cookie survived). Stand:
 * tests/playwright/work-survives-disk-pressure.mjs.
 *
 * So where the browser has Storage Buckets (Chromium), work is written to a
 * separate bucket named `work`, opened with:
 *   persisted: true   — the same silent heuristic as storage.persist()
 *                       (engagement, bookmark, installed app — no prompt).
 *                       A persisted bucket is never evicted.
 *   durability:'strict' — a write reports success only after it has reached
 *                       the disk, so a machine that loses power right after a
 *                       save still has it. Measured 29.09.2026 in Chrome 153: a
 *                       design save with 4.2 MB of photos costs 20–28 ms, a
 *                       settings-only save 1–2 ms; strict adds at most ~9 ms.
 * Elsewhere (Firefox asks the person before persisting; WebKit has no buckets)
 * the default IndexedDB is used, as before, with strict transactions.
 *
 * ── Why not localStorage ────────────────────────────────────────────────────
 * Measured 29.09.2026 (tests/scratch/storage-crash-probe-scratch.mjs, Chrome
 * 153, the whole process tree killed with taskkill /F, which is what a browser
 * or PC crash does): an IndexedDB write that had completed survived the kill
 * 6 times out of 6, even when the kill came 0 ms after the write. A
 * localStorage write was lost 9 times out of 9 when the kill came within
 * 35 s, and was on disk only after 35–70 s. Chromium commits localStorage to
 * disk at most about once a minute, so a crash loses the last minute of it.
 * localStorage is fine for settings. It must not hold the work itself.
 *
 * ── Knowing that the browser took it ────────────────────────────────────────
 * Eviction happens silently. On the next visit the store is simply empty, and
 * the person concludes that «saved automatically» was a lie. Each
 * incarnation of the work storage has an EPOCH, a random id stored inside it.
 * An eviction deletes the whole bucket, epoch included, so the next page finds
 * a new epoch. A store that noted in localStorage which epoch its records were
 * written under (localStorage is not evicted) can then tell «the browser
 * deleted this» apart from «there never was anything». See tool-draft.js
 * takeLost().
 *
 * Exposes window.__workStorage:
 *   open(name, version, upgrade, {stamp}) → Promise<{db, mode:'b'|'d', bucket}>
 *   tx(db, stores)                         → a readwrite, strict transaction
 *   epoch()                                → Promise<string|null>
 *   protect(origin)                        → Promise<'granted'|'denied'|''>
 *   protection()                           → Promise<{bucket, persisted}>
 */
(function (global) {
'use strict';
if (global.__workStorage) return;

var BUCKET = 'work';
var EPOCH_DB = 'timbrica_work_epoch';
var ASK_AGAIN_MS = 15 * 60000;

function bucketsApi() {
    try {
        var s = global.navigator && global.navigator.storageBuckets;
        return !!(s && typeof s.open === 'function');
    } catch (_) { return false; }
}

var _bucketP = null, _bucket = null;
/** The `work` bucket, or null where there are no buckets (or opening failed). */
function bucket() {
    if (_bucketP) return _bucketP;
    _bucketP = !bucketsApi() ? Promise.resolve(null)
        : global.navigator.storageBuckets.open(BUCKET, { persisted: true, durability: 'strict' })
            .then(function (b) { _bucket = b; return b; }, function () { return null; });
    return _bucketP;
}

/* create=false opens an EXISTING database only: an upgrade from version 0 is
   aborted, so probing for a legacy database never creates an empty one.
   version null opens whatever version is there, without an upgrade. */
function idbOpen(factory, name, version, upgrade, create) {
    return new Promise(function (resolve, reject) {
        var r;
        try { r = version == null ? factory.open(name) : factory.open(name, version); } catch (e) { reject(e); return; }
        r.onupgradeneeded = function (ev) {
            if (!create) { try { r.transaction.abort(); } catch (_) {} return; }
            try { upgrade(r.result, ev.oldVersion || 0, r.transaction); } catch (e) { try { r.transaction.abort(); } catch (_) {} }
        };
        r.onsuccess = function () { resolve(r.result); };
        r.onerror = function () { reject(r.error || new Error('idb-open')); };
        r.onblocked = function () { reject(new Error('idb-blocked')); };
    });
}

/** A readwrite transaction that reports success only once the write is on disk. */
function tx(db, stores) {
    try { return db.transaction(stores, 'readwrite', { durability: 'strict' }); }
    catch (_) { return db.transaction(stores, 'readwrite'); }
}

function report(f, data) {
    try { if (typeof global.__ae === 'function') global.__ae('feature_use', Object.assign({ f: f }, data || {})); } catch (_) {}
}

/* Records written before the bucket existed, or by a tab still running an older
   script, sit in the default storage under the same database name. They are
   moved over, newest winning by `stamp`, and then removed there. This is
   best-effort: a failure leaves them in place and the next page tries again.
   The default database is opened only if it exists, so a fresh browser does not
   end up with an empty one. Values that hold Blobs are copied with their bytes:
   the new database gets its own copy. */
function migrate(db, name, stamp) {
    var idb = global.indexedDB;
    if (!idb) return Promise.resolve(0);
    stamp = stamp || function () { return 0; };
    var listed = typeof idb.databases === 'function' ? idb.databases() : Promise.resolve([{ name: name }]);
    return Promise.resolve(listed).then(function (dbs) {
        if (!(dbs || []).some(function (d) { return d && d.name === name; })) return 0;
        return idbOpen(idb, name, null, function () {}, false).then(function (old) {
            var stores = Array.prototype.filter.call(old.objectStoreNames, function (s) {
                return db.objectStoreNames.contains(s);
            });
            if (!stores.length) { old.close(); return 0; }
            return new Promise(function (resolve) {
                var rows = [];     // {store, key, value}
                var rt = old.transaction(stores, 'readonly');
                stores.forEach(function (s) {
                    rt.objectStore(s).openCursor().onsuccess = function (e) {
                        var c = e.target.result;
                        if (c) { rows.push({ store: s, key: c.primaryKey, value: c.value }); c.continue(); }
                    };
                });
                rt.onerror = rt.onabort = function () { old.close(); resolve(0); };
                rt.oncomplete = function () {
                    if (!rows.length) { old.close(); resolve(0); return; }
                    var wt = tx(db, stores);
                    rows.forEach(function (row) {
                        var st = wt.objectStore(row.store);
                        var g = st.get(row.key);
                        g.onsuccess = function () {
                            var have = g.result;
                            if (have !== undefined && stamp(have) >= stamp(row.value)) return;
                            if (st.keyPath == null) st.put(row.value, row.key); else st.put(row.value);
                        };
                    });
                    wt.onerror = wt.onabort = function () { old.close(); resolve(0); };
                    wt.oncomplete = function () {
                        var dt = old.transaction(stores, 'readwrite');
                        rows.forEach(function (row) { dt.objectStore(row.store).delete(row.key); });
                        dt.oncomplete = function () { old.close(); resolve(rows.length); };
                        dt.onerror = dt.onabort = function () { old.close(); resolve(0); };
                    };
                };
            });
        }, function () { return 0; });
    }).catch(function () { return 0; });
}

/**
 * Open (creating as needed) the database `name` where work lives.
 * `upgrade(db, oldVersion, tx)` creates the stores; `opts.stamp(value)` →
 * number orders a legacy record against one already in the bucket.
 */
function open(name, version, upgrade, opts) {
    opts = opts || {};
    var idb = global.indexedDB;
    function viaDefault() {
        if (!idb) return Promise.reject(new Error('no-idb'));
        return idbOpen(idb, name, version, upgrade, true).then(function (db) {
            return { db: db, mode: 'd', bucket: null };
        });
    }
    return bucket().then(function (b) {
        if (!b) return viaDefault();
        return idbOpen(b.indexedDB, name, version, upgrade, true).then(function (db) {
            return migrate(db, name, opts.stamp).then(function (n) {
                if (n) report('work_store_migrated', { db: name, n: n });
                return { db: db, mode: 'b', bucket: b };
            });
        }, viaDefault);
    });
}

/**
 * A connection to `name` that outlives the browser taking it away.
 *
 * The browser closes an open database under a live page when it evicts the
 * storage, when site data is cleared, or (the `versionchange` path) when another
 * page needs the database. A store that keeps the first connection for the life
 * of the page then fails every later read and write — «The database connection
 * is closing» (Sentry TIMBRICA-FRONTEND-RV/RW, /ebook-reader in Edge: reading
 * stats stopped being written for three hours), «A mutation operation was
 * attempted on a database that did not allow mutations» (RX, a draft in
 * Firefox). Measured 29.10.2026 (tests/scratch/draft-forced-close-scratch.mjs,
 * Chromium, the work bucket deleted under /text-to-handwriting): the next edit
 * was never saved and nothing on the page said so.
 *
 * connection(name, version, upgrade, opts) → {
 *   db()               Promise<IDBDatabase> — a live one, opened again after a close
 *   tx(stores, mode)   Promise<IDBTransaction> — mode 'readwrite' (default, strict
 *                      durability) or 'readonly'; a connection found closing when
 *                      the transaction is created is replaced once
 *   mode()             'b' bucket, 'd' default, '' not open yet
 *   onReopen(fn)       fn(handle) after the BROWSER's close was replaced by a new
 *                      connection — what was stored may be gone with it (epoch())
 * }
 * Use the transaction in the task it is handed over in (a .then / await of tx()),
 * as with any IndexedDB transaction.
 */
function connection(name, version, upgrade, opts) {
    var cur = null, live = null, mode = '', taken = false, reopened = [];
    function drop(d, why) {
        if (live !== d) return;
        live = null; cur = null;
        if (why !== 'versionchange') {
            // the browser took it: the bucket and its epoch may have gone with it
            taken = true; _bucketP = null; _bucket = null; _epochP = null;
        }
        report('work_store_closed', { db: String(name).slice(0, 40), why: why });
    }
    function db() {
        if (cur) return cur;
        var p = cur = open(name, version, upgrade, opts).then(function (h) {
            var d = h.db;
            live = d; mode = h.mode;
            try { d.addEventListener('close', function () { drop(d, 'close'); }); } catch (_) {}
            // another page needs the database (an upgrade, a delete): let it, and open again on next use
            try { d.addEventListener('versionchange', function () { try { d.close(); } catch (_) {} drop(d, 'versionchange'); }); } catch (_) {}
            if (taken) {
                taken = false;
                reopened.slice().forEach(function (fn) { try { fn(h); } catch (_) {} });
            }
            return d;
        });
        p.catch(function () { if (cur === p) cur = null; });
        return p;
    }
    function create(d, stores, m) { return m === 'readonly' ? d.transaction(stores, 'readonly') : tx(d, stores); }
    return {
        db: db,
        tx: function (stores, m) {
            return db().then(function (d) {
                try { return create(d, stores, m); }
                catch (e) {
                    // closed without an event reaching us yet (WebKit, or the close is in flight)
                    if (!(e && (e.name === 'InvalidStateError' || e.code === 11))) throw e;
                    drop(d, 'closing');
                    return db().then(function (d2) { return create(d2, stores, m); });
                }
            });
        },
        mode: function () { return mode; },
        onReopen: function (fn) { reopened.push(fn); }
    };
}

var _epochP = null;
/**
 * Id of the current incarnation of the work storage, prefixed with its mode
 * ('b:…' bucket, 'd:…' default). An eviction or a «clear site data» deletes it
 * together with every record, so a record noted under another epoch is gone for
 * a reason that was not ours. null when it cannot be read.
 */
function epoch() {
    if (_epochP) return _epochP;
    _epochP = bucket().then(function (b) {
        var factory = b ? b.indexedDB : global.indexedDB;
        if (!factory) return null;
        return idbOpen(factory, EPOCH_DB, 1, function (db) { db.createObjectStore('e'); }, true).then(function (db) {
            return new Promise(function (resolve) {
                var t = tx(db, 'e'), st = t.objectStore('e'), v = null;
                var g = st.get('epoch');
                g.onsuccess = function () {
                    v = g.result;
                    if (!v || !v.id) {
                        v = { id: Date.now().toString(36) + Math.random().toString(36).slice(2, 8), at: Date.now() };
                        st.put(v, 'epoch');
                    }
                };
                t.oncomplete = function () { try { db.close(); } catch (_) {} resolve((b ? 'b:' : 'd:') + v.id); };
                t.onerror = t.onabort = function () { try { db.close(); } catch (_) {} resolve(null); };
            });
        });
    }).catch(function () { return null; });
    return _epochP;
}

/* Protection is requested only when there is something worth protecting (the
   caller decides that): first when the first real record is written, then again
   after a quarter of an hour of work. Chromium's answer depends on site
   engagement, and that work itself raises it. Nothing is requested outside a
   bucket, because Firefox shows a permission prompt, and a prompt nobody asked
   for is worse than an eviction nobody hits. */
var _askedAt = 0, _state = '', _defaultReported = false;
function protect(origin) {
    origin = String(origin || '').slice(0, 32);
    return bucket().then(function (b) {
        if (!b) {
            if (!_defaultReported) {
                _defaultReported = true;
                var st = global.navigator && global.navigator.storage;
                Promise.resolve(st && st.persisted ? st.persisted() : false).then(function (p) {
                    report('work_store', { bucket: 0, persisted: p ? 1 : 0, via: 'default', origin: origin });
                }).catch(function () {});
            }
            return '';
        }
        if (_state === 'granted') return _state;
        var now = Date.now();
        if (_askedAt && now - _askedAt < ASK_AGAIN_MS) return _state;
        var first = !_askedAt;
        _askedAt = now;
        return Promise.resolve(b.persisted()).then(function (p) {
            return p ? 'already' : Promise.resolve(b.persist()).then(function (g) { return g ? 'granted' : 'denied'; });
        }).then(function (s) {
            var prev = _state;
            _state = s === 'already' ? 'granted' : s;
            if (first || _state !== prev) report('work_store', { bucket: 1, persisted: _state === 'granted' ? 1 : 0, via: s, origin: origin });
            return _state;
        });
    }).catch(function () { return _state; });
}

/** Which storage holds the work on this page and whether the browser promised to keep it. */
function protection() {
    return bucket().then(function (b) {
        if (!b) return { bucket: false, persisted: false };
        return Promise.resolve(b.persisted()).then(function (p) { return { bucket: true, persisted: !!p }; });
    }).catch(function () { return { bucket: false, persisted: false }; });
}

global.__workStorage = {
    BUCKET: BUCKET,
    open: open,
    connection: connection,
    tx: tx,
    epoch: epoch,
    protect: protect,
    protection: protection
};
})(typeof window !== 'undefined' ? window : this);
